import router from '@/router'
import axios from 'axios'
import { ElMessage } from 'element-plus'

// 创建axios实例
const request = axios.create({
  baseURL: '/api/v1',
  timeout: 60000, // 1分钟超时（用于调试页面）
  headers: {
    'Content-Type': 'application/json',
  }
})

// 简单的认证错误事件总线
const authEventBus = {
  listeners: [],
  onAuthError(callback) {
    this.listeners.push(callback)
  },
  emitAuthError(message) {
    this.listeners.forEach(callback => {
      try {
        callback(message)
      } catch (error) {
        console.error('认证错误处理回调执行失败:', error)
      }
    })
  }
}

// 请求拦截器
request.interceptors.request.use(
  config => {
    // 添加认证token
    const token = localStorage.getItem('access_token')
    const tokenType = localStorage.getItem('token_type') || 'Bearer'
    if (token) {
      config.headers.Authorization = `${tokenType} ${token}`
    }

    // 添加请求ID用于追踪
    config.headers['X-Request-ID'] = Date.now().toString()

    return config
  },
  error => {
    console.error('请求拦截器错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
request.interceptors.response.use(
  response => {
    const { data, status, config } = response

    // 检查HTTP状态码
    if (status >= 200 && status < 300) {
      // 检查是否是文件下载API（通过URL判断）
      if (config.url && config.url.includes('/generate-and-download')) {
        // 直接返回原始响应，让前端处理文件下载
        return response
      }

      // 严格按照后端响应格式处理
      if (data.success === true) {
        // 成功响应，返回data字段
        return {
          success: true,
          data: data.data,
          message: data.message,
          requestId: data.request_id,
          timestamp: data.timestamp,
          pagination: data.pagination,
          meta: data.meta
        }
      } else if (data.success === false) {
        // 业务逻辑错误
        const message = data.message || '请求失败'
        // 检查是否有验证错误
        if (data.errors && typeof data.errors === 'object') {
          // 处理验证错误
          const errorMessages = []
          Object.keys(data.errors).forEach(field => {
            const fieldErrors = data.errors[field]
            if (Array.isArray(fieldErrors)) {
              // 将字段的所有错误信息添加到数组中
              errorMessages.push(...fieldErrors)
            } else if (typeof fieldErrors === 'string') {
              // 单个错误字符串
              errorMessages.push(fieldErrors)
            }
          })

          // 显示所有验证错误
          if (errorMessages.length > 0) {
            errorMessages.forEach(errorMsg => {
              ElMessage.error(errorMsg)
            })
          } else {
            ElMessage.error(message)
          }
        } else {
          // 普通业务错误
          ElMessage.error(message)
        }

        return Promise.reject({
          type: 'business',
          message,
          errors: data.errors,
          requestId: data.request_id,
          timestamp: data.timestamp
        })
      } else {
        // 没有success字段，可能是直接返回数据
        return {
          success: true,
          data: data,
          message: '请求成功'
        }
      }
    } else {
      // HTTP状态码错误
      const message = data?.message || `HTTP错误: ${status}`
      ElMessage.error(message)
      return Promise.reject({
        type: 'http',
        status,
        message,
        data: data
      })
    }
  },
  error => {
    console.error('响应拦截器错误:', error)

    // 网络错误
    if (!error.response) {
      ElMessage.error('网络连接失败，请检查网络设置')
      return Promise.reject({
        type: 'network',
        message: '网络连接失败'
      })
    }

    const { status, data } = error.response

    // 根据状态码处理不同类型的错误
    let errorMessage = ''

    switch (status) {
      case 400:
        // 请求参数错误
        errorMessage = data?.message || '请求参数错误'
        ElMessage.error(errorMessage)
        break

      case 401:
        // 认证失败 - 使用事件总线通知
        errorMessage = data?.message || '登录已过期，请重新登录'
        handleAuthError(errorMessage)
        break

      case 403:
        // 权限不足
        errorMessage = data?.message || '权限不足，无法访问此资源'
        ElMessage.error(errorMessage)
        break

      case 404:
        // 资源不存在
        errorMessage = data?.message || '请求的资源不存在'
        ElMessage.error(errorMessage)
        break

      case 409:
        // 资源冲突
        errorMessage = data?.message || '资源冲突，请检查输入信息'
        ElMessage.error(errorMessage)
        break

      case 422:
        // 参数校验失败
        errorMessage = data?.message || '请求参数验证失败'

        // 检查是否有验证错误
        if (data?.errors && typeof data.errors === 'object') {
          // 处理验证错误
          const errorMessages = []
          Object.keys(data.errors).forEach(field => {
            const fieldErrors = data.errors[field]
            if (Array.isArray(fieldErrors)) {
              // 将字段的所有错误信息添加到数组中
              errorMessages.push(...fieldErrors)
            } else if (typeof fieldErrors === 'string') {
              // 单个错误字符串
              errorMessages.push(fieldErrors)
            }
          })

          // 显示所有验证错误
          if (errorMessages.length > 0) {
            errorMessages.forEach(errorMsg => {
              ElMessage.error(errorMsg)
            })
          } else {
            ElMessage.error(errorMessage)
          }
        } else {
          ElMessage.error(errorMessage)
        }
        break

      case 429:
        // 请求频率限制
        errorMessage = data?.message || '请求过于频繁，请稍后再试'
        ElMessage.error(errorMessage)
        break

      case 500:
        // 服务器内部错误
        errorMessage = data?.message || '服务器内部错误，请稍后再试'
        ElMessage.error(errorMessage)
        break

      default:
        // 其他错误
        errorMessage = data?.message || `请求失败 (${status})`
        ElMessage.error(errorMessage)
    }

    return Promise.reject({
      type: 'http',
      status,
      message: data?.message || `HTTP错误: ${status}`,
      data: data,
      requestId: data?.request_id,
      timestamp: data?.timestamp
    })
  }
)

// 处理认证错误的函数
const handleAuthError = (message) => {
  // 清理localStorage
  localStorage.removeItem('access_token')
  localStorage.removeItem('token_type')

  // 通过事件总线通知其他组件
  authEventBus.emitAuthError(message)

  // 显示错误消息
  ElMessage.error(message)

  const currentPath = router.currentRoute.value.path || ''
  const loginPath = currentPath.startsWith('/sub/') ? '/sub/auth/login' : '/auth/login'

  // 跳转到登录页面（如果不在登录页面）
  if (currentPath !== loginPath) {
    router.push(loginPath)
  }
}

// 导出请求实例
export default request

// 导出认证事件总线
export { authEventBus }

// 导出响应处理工具函数
export const handleResponse = (response) => {
  if (response.success) {
    return response.data
  }
  throw new Error(response.message || '请求失败')
}

// 导出错误处理工具函数
export const handleError = (error) => {
  if (error.type === 'business') {
    // 业务逻辑错误
    return {
      success: false,
      message: error.message,
      errors: error.errors
    }
  } else if (error.type === 'http') {
    // HTTP错误
    return {
      success: false,
      message: error.message,
      status: error.status
    }
  } else if (error.type === 'network') {
    // 网络错误
    return {
      success: false,
      message: error.message
    }
  }

  // 未知错误
  return {
    success: false,
    message: '未知错误'
  }
}
